import { useEffect } from 'react';

/**
 * Helper to provide click listener to close an overlay if clicking outside of
 * that element.
 *
 * @param {Array} exclude The list of classes to ignore firing the
 *  onClickOutside event.
 * @param {Function} onClickOutside The method to fire if we are clicking
 *  outside of the overlay.
 */
const useCloseOverlay = (
  exclude: string[],
  onClickOutside: () => void
) => {
  // setup click handler for when mega nav is open, to be able to close it when
  // the user clicks outside of it
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      // start with element just clicked
      let currEl = e.target as HTMLElement;

      const clearCurrentEl = () => {
        currEl = null;
      };

      while (currEl) {
        const { classList } = currEl;

        // check if this element contains any of the exclude classes
        exclude.forEach((selector) => {
          if (classList.contains(selector)) {
            clearCurrentEl();
          }
        });

        // if we are clicking within one of the exclude elements, we can stop
        // checking other parent elements and just carry on as per normal
        if (!currEl) {
          return;
        }

        // otherwise we keep checking parent elements to see if we are within
        // an element we want to prevent closing the mega nav
        currEl = currEl.parentElement;
      }

      // if we got here, it means we aren't clicking the mega nav or the main
      // nav, so we want to close the mega nav
      onClickOutside();
    }

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [exclude, onClickOutside]);
};

export default useCloseOverlay;
